import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';

/**
 * Nav — editorial flight-deck sidebar (paper-2 bg, mono-numbered items, teal
 * active-border). Wired to react-router: items carry a `to` route, the active
 * item is derived from the current location, and clicking navigates.
 *
 *   - Expanded 260px: full numbered + labeled item list grouped by section
 *   - Collapsed 56px: number-only mini-strip with group separators
 *   - Auto-collapses below a narrow/phone width so the bench scanning flow keeps
 *     its full-width tap targets; a manual toggle (once used) wins thereafter.
 */

const NARROW_PX = 768;

export interface NavItem {
  id: string;
  num: string;
  name: string;
  to: string;
  meta?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export interface NavProps {
  groups: NavGroup[];
}

interface StripItem extends NavItem {
  groupIndex: number;
}

function isItemActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(to + '/');
}

export function Nav({ groups }: NavProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const initialNarrow = typeof window !== 'undefined' && window.innerWidth < NARROW_PX;
  const [collapsed, setCollapsed] = useState(initialNarrow);
  const userSet = useRef(false);

  // Follow viewport width until the user takes manual control of the toggle.
  useEffect(() => {
    const onResize = () => {
      if (!userSet.current) setCollapsed(window.innerWidth < NARROW_PX);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggle = () => {
    userSet.current = true;
    setCollapsed((c) => !c);
  };

  const go = (to: string) => navigate(to);

  // Flat strip for the collapsed view — numbered items with separators between groups.
  const stripItems: StripItem[] = groups.flatMap((g, gi) =>
    g.items.map((it) => ({ ...it, groupIndex: gi })),
  );

  return (
    <nav className={cn(collapsed && 'collapsed')} aria-label="primary navigation">
      <button
        type="button"
        className="nav-toggle"
        onClick={toggle}
        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-expanded={!collapsed}
      >
        {collapsed ? '▶' : '◀'}
      </button>

      {/* Expanded: full grouped list */}
      <div className="nav-body side-pad">
        {groups.map((g) => (
          <div key={g.label} className="side-group">
            <div className="side-label">{g.label}</div>
            {g.items.map((item) => {
              const active = isItemActive(location.pathname, item.to);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => go(item.to)}
                  className={cn('side-item', active && 'active')}
                  style={{ width: '100%', textAlign: 'left', textDecoration: 'none' }}
                  aria-current={active ? 'page' : undefined}
                >
                  <span className="num">{item.num}</span>
                  <span className="name">{item.name}</span>
                  <span className="meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {item.meta && <span>{item.meta}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Collapsed: number-only mini-strip with group separators */}
      <div className="nav-strip">
        {stripItems.map((it, i) => {
          const active = isItemActive(location.pathname, it.to);
          const prev = i > 0 ? stripItems[i - 1] : undefined;
          const showSep = prev !== undefined && it.groupIndex !== prev.groupIndex;
          return (
            <Fragment key={it.id}>
              {showSep && <div className="nav-strip-sep" />}
              <button
                type="button"
                onClick={() => go(it.to)}
                className={cn('nav-strip-item', active && 'active')}
                title={`${it.num} · ${it.name}`}
                aria-label={`${it.num} ${it.name}`}
                aria-current={active ? 'page' : undefined}
                style={{ textDecoration: 'none' }}
              >
                <span className="num">{it.num}</span>
              </button>
            </Fragment>
          );
        })}
      </div>
    </nav>
  );
}
